import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

/**
 * The dark theme is not finished, so the application does not activate it.
 *
 * BugHerd #144: only a handful of the ~166 components define `dark:` variants, so applying
 * `html.dark` leaves fixed `text-gray-900` content sitting on dark card surfaces at 1.21:1,
 * and page headings at 1.00:1 - far below the WCAG AA thresholds of 4.5:1 for normal text
 * and 3:1 for large text. System Settings already advertises Dark Mode as "Coming Soon";
 * this flag makes the rest of the application agree with that.
 *
 * Flip to `true` once dark token coverage is complete. Nothing else has to change: the
 * provider API, every `useTheme()` consumer and any previously stored preference are all
 * left intact.
 */
export const DARK_MODE_ENABLED = false;

const STORAGE_KEY = 'theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // While the dark theme is unfinished the app stays light regardless of any stored or
    // system preference. A stored value is read-gated rather than deleted, so a user's
    // earlier choice returns intact if the flag is switched back on.
    if (!DARK_MODE_ENABLED) {
      return 'light';
    }

    const savedTheme = localStorage.getItem(STORAGE_KEY);
    if (savedTheme === 'light' || savedTheme === 'dark') {
      return savedTheme;
    }

    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    return 'light';
  });

  useEffect(() => {
    // Apply the theme to the document. The remove() branch also clears a stale `dark` class
    // left on the element by an earlier session.
    const root = document.documentElement;

    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  useEffect(() => {
    if (!DARK_MODE_ENABLED) {
      return;
    }

    // Follow the system preference, but only until the user chooses explicitly. This works
    // because the preference is written on explicit action only (see applyTheme); writing it
    // on every mount would make this listener permanently dead code.
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const applyTheme = (next: Theme) => {
    if (!DARK_MODE_ENABLED) {
      return;
    }

    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const toggleTheme = () => {
    applyTheme(theme === 'light' ? 'dark' : 'light');
  };

  const setTheme = (newTheme: Theme) => {
    applyTheme(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    toggleTheme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
